import { DriverDto, ProductLine } from '@trucktrax/trucktrax-ts-common';
import { sortBy } from 'lodash';
import { createSelector } from 'reselect';
import { ReduxState } from '..';
import { MapMarker, MapMarkerType, PositionDetailsDto } from '../../types';
import {
  convertDriverMarkersToTruckMarkers,
  createTrackableMapMarkersFromPositions,
  determineMarkerPosition,
  groupMarkersByType,
} from '../../util/markerUtil';
import { TRUCKTRAX_GEO_FLAG } from '../../constants/appConstants';
import { isFeatureFlagEnabled } from '../../util/featureFlagUtil';
import { getIdFromUrl } from '../../util/appUtil';
import { mapFilterInitialState, PlantFilterOption } from '../reducers/mapFilterReducers';
import { filterMarkersBasedOnWorkflow, WorkFlowProductLines } from '../../util/statusesUtil';
import { shouldDisplayPosition } from '../../util/positionsUtil';
import { RegionAccessMap } from '../../util/permissionUtil';

export type TruckDesignations = {
  internal: MapMarker[];
  external: MapMarker[];
};

type PositionStateSlice = { regionUrl: string; positions: PositionDetailsDto[] };
const positionsStateSlice = (state: Partial<ReduxState>): PositionStateSlice => {
  const { positionList: positions = [], currentRegion: { url: regionUrl = '' } = { url: '' } } =
    state;
  return { regionUrl, positions };
};

export const mapMarkerSelector = createSelector(
  [positionsStateSlice],
  (data: PositionStateSlice) => {
    const { positions, regionUrl } = data;
    const mapMarkers = createTrackableMapMarkersFromPositions(positions, regionUrl);
    return mapMarkers;
  },
);

type ActiveMarkersStateSlice = Pick<
  ReduxState,
  | 'positionList'
  | 'mapFilters'
  | 'plantList'
  | 'allTrucks'
  | 'allDrivers'
  | 'currentRegion'
  | 'userPermission'
  | 'selectedProductLine'
  | 'activeMarkers'
>;
const activeMarkersStateSlice = (state: ReduxState): ActiveMarkersStateSlice => {
  const {
    positionList = [],
    mapFilters = { ...mapFilterInitialState },
    plantList = [],
    allTrucks = [],
    allDrivers = [],
    currentRegion = {},
    userPermission = {},
    selectedProductLine = ProductLine.None,
    activeMarkers = [],
  } = state;

  return {
    positionList,
    mapFilters,
    plantList,
    allTrucks,
    allDrivers,
    currentRegion,
    userPermission,
    activeMarkers,
    selectedProductLine,
  };
};

interface ActiveMarkersFilter {
  showInternal: boolean;
  showExternal: boolean;
  plantList: PlantFilterOption[];
}

export const activeMarkersSelector = createSelector(
  activeMarkersStateSlice,
  (state: ActiveMarkersStateSlice): MapMarker[] => {
    const {
      positionList,
      mapFilters: { showExtTrucks: showExternal, showTrucks: showInternal, plantList },
      allTrucks,
      allDrivers,
      currentRegion,
      userPermission,
      selectedProductLine,
    } = state;
    const regionUrl = currentRegion?.url ?? '';
    const filters = { showExternal, showInternal, plantList };
    const isTruckTraxGeoEnabled = isFeatureFlagEnabled(
      userPermission,
      regionUrl,
      TRUCKTRAX_GEO_FLAG,
    );
    const markers = createTrackableMapMarkersFromPositions(
      positionList,
      regionUrl,
      allDrivers,
      allTrucks,
      isTruckTraxGeoEnabled,
    );
    const activeMarkers = determineActiveMarkersInRegionAndPlant(markers, filters);
    const { Truck: activeTrucks, Driver: activeDrivers } = groupMarkersByType(activeMarkers);
    const { convertedTruckMarkers, removedDrivers } = convertDriverMarkersToTruckMarkers(
      activeDrivers,
      allTrucks,
    );
    const uniqueActiveDrivers = activeDrivers.filter(driver => !removedDrivers.includes(driver.id));
    const seenIds = new Set<string>();
    const combinedMarkers: MapMarker[] = [];
    [...activeTrucks, ...convertedTruckMarkers, ...uniqueActiveDrivers].forEach(marker => {
      if (!seenIds.has(marker.id)) {
        seenIds.add(marker.id);
        combinedMarkers.push(marker);
      }
    });
    const markersMatchingWorkflow = filterMarkersBasedOnWorkflow(
      combinedMarkers,
      selectedProductLine as WorkFlowProductLines,
    );
    const sortedCombinedActiveMarkers = sortBy(markersMatchingWorkflow, 'id');
    return sortedCombinedActiveMarkers ?? [];
  },
);

type AllDriversStateSlice = {
  allDrivers: DriverDto[];
  userPermission: RegionAccessMap;
  regionUrl: string | undefined;
};

const allDriversStateSlice = (state: ReduxState): AllDriversStateSlice => ({
  allDrivers: state.allDrivers,
  userPermission: state.userPermission,
  regionUrl: state.currentRegion?.url,
});

export const selectMapMarkerFolderDesignations = createSelector(
  activeMarkersSelector,
  allDriversStateSlice,
  (activeMarkers: MapMarker[], state: AllDriversStateSlice) => {
    const isTruckTraxGeoEnabled = isFeatureFlagEnabled(
      state.userPermission,
      state.regionUrl ?? '',
      TRUCKTRAX_GEO_FLAG,
    );
    const sortedDrivers = (state.allDrivers as DriverDto[])?.sort((a, b) => a.id - b.id) ?? [];
    const internal: MapMarker[] = [];
    const external: MapMarker[] = [];
    activeMarkers.forEach((truck: MapMarker) => {
      const { driverUrl: targetDriverUrl } = truck.data;
      if (!targetDriverUrl) {
        return;
      }

      if (!isTruckTraxGeoEnabled) {
        internal.push(truck);
      } else {
        const matchingDriver = sortedDrivers.find(
          (driver: DriverDto) => driver.url === targetDriverUrl,
        );
        if (matchingDriver?.isExternal === true) {
          external.push(truck);
        } else if (matchingDriver) {
          internal.push(truck);
        }
      }
    });

    return {
      internal,
      external,
    };
  },
);

function determineActiveMarkersInRegionAndPlant(
  markers: MapMarker[],
  filters: ActiveMarkersFilter,
): MapMarker[] {
  const markersMatchingMapFilters = filterMarkersBySelectedPlants(markers, filters.plantList);
  const markersWithNoTicket = filterMarkersWithNoTicketExcludeDuplicates(
    markers,
    markersMatchingMapFilters,
    filters,
  );
  const combinedMarkers = [...markersMatchingMapFilters, ...markersWithNoTicket];
  const activeMarkers = filterMarkersByActive(combinedMarkers, filters);
  return activeMarkers;
}

function filterMarkersByActive(markers: MapMarker[], filters: ActiveMarkersFilter): MapMarker[] {
  const { showInternal, showExternal } = filters;
  const activeMarkers: MapMarker[] = [];
  markers.forEach((marker: MapMarker) => {
    const isInternal = marker.data.isExternal === false;
    const isExternal = marker.data.isExternal === true;
    const canShowInternal = (showInternal ?? true) && isInternal;
    const canShowExternal = (showExternal ?? false) && isExternal;
    const isFilteredOn = canShowInternal || canShowExternal;
    const markerPosition = determineMarkerPosition(marker);
    const canRenderMarker = shouldDisplayPosition(markerPosition);

    if (isFilteredOn && canRenderMarker) {
      marker.data.isActive = true;
      activeMarkers.push(marker);
    } else {
      marker.data.isActive = false;
    }
  });
  return activeMarkers;
}

function filterMarkersBySelectedPlants(markers: MapMarker[], plantList: any[]): MapMarker[] {
  const selectedPlantIds =
    plantList?.filter(plant => plant.isSelected).map((plant: any) => plant.id) || [];
  const selectedPlantIdsSet = new Set(selectedPlantIds);

  const filteredMarkers: MapMarker[] = markers.filter((marker: MapMarker) => {
    const { ticketUrl, plantUrl } = marker.data;

    // Trucks and drivers without tickets will be handled by filterMarkersWithNoTicketExcludeDuplicates
    if (marker.type === MapMarkerType.Truck && (!ticketUrl || !plantUrl)) {
      return false;
    }
    if (marker.type === MapMarkerType.Driver && !ticketUrl && !plantUrl) {
      return false;
    }

    // Check if the truck's plant ID is in the selectedPlantIdsSet
    const ticketPlantId = Number(getIdFromUrl(plantUrl));
    return !Number.isNaN(ticketPlantId) && selectedPlantIdsSet.has(ticketPlantId);
  });
  return filteredMarkers;
}

function filterMarkersWithNoTicketExcludeDuplicates(
  markers: MapMarker[],
  markersMatchingMapFilters: MapMarker[],
  filters: ActiveMarkersFilter,
): MapMarker[] {
  const { plantList = [], showInternal, showExternal } = filters;
  const markerIds: string[] = markersMatchingMapFilters.map((marker: MapMarker) => marker.id);
  const selectedPlantIds = plantList.filter(plant => plant.isSelected).map(plant => plant.id);
  const selectedPlantIdsSet = new Set(selectedPlantIds);

  const markersWithNoTicket: MapMarker[] = [];
  if (showInternal || showExternal) {
    markers.forEach((marker: MapMarker) => {
      const { ticketUrl, plantUrl, isExternal } = marker.data;
      const hasNoTicket = !ticketUrl;
      const notAlreadyIncluded = !markerIds.includes(marker.id);

      if (hasNoTicket && notAlreadyIncluded) {
        // DEV-2749: Filter non-ticketed trucks by their default plant
        // If no plants are selected, show all; otherwise filter by selected plants
        const plantId = Number(getIdFromUrl(plantUrl));
        const matchesPlantFilter =
          selectedPlantIdsSet.size === 0 ||
          (!Number.isNaN(plantId) && selectedPlantIdsSet.has(plantId));

        // DEV-2749: If the driver is external, show it regardless of plant filter
        if (matchesPlantFilter || isExternal) {
          markersWithNoTicket.push(marker);
        }
      }
    });
  }

  return markersWithNoTicket;
}
